import './styles.scss';

import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import Footer from 'examples/Footer';

import {Axios} from 'services/axios';
import {Loading, Notify, Confirm} from 'notiflix';
import {useForm, useFieldArray} from 'react-hook-form';

// Import the additional features components
import AdditionalFeatureCategoriesTable from './AdditionalFeatureCategoriesTable';
import AdditionalFeaturesTable from './AdditionalFeaturesTable';

/* Helper component to render tab panels */
function TabPanel(props) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`additional-tabpanel-${index}`}
      aria-labelledby={`additional-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

/* Define table columns for the main features */
const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Feature Name', accessor: 'name', width: '20%', align: 'left'},
  {
    Header: 'Description',
    accessor: 'description',
    width: '20%',
    align: 'center',
  },
  {
    Header: 'Feature Default Price',
    accessor: 'featureDefaultPrice',
    width: '20%',
    align: 'center',
  },
  {Header: 'Created at', accessor: 'createdOn', align: 'center'},
  {Header: 'Updated at', accessor: 'updatedOn', align: 'center'},
  {Header: 'Status', accessor: 'status', align: 'center'},
  {Header: 'Action', accessor: 'action', align: 'center'},
];

/* Build table row elements for main features */
const getRow = (row, idx, handleEdit, handleDeleteFeature) => ({
  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  name: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {row.name}
    </MDTypography>
  ),
  description: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {row.description}
    </MDTypography>
  ),
  featureDefaultPrice: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {row.featureDefaultPrice}
    </MDTypography>
  ),
  createdOn: (
    <MDTypography variant="caption">
      {moment(row.createdAt).calendar()}
    </MDTypography>
  ),
  updatedOn: (
    <MDTypography variant="caption">
      {moment(row.updatedAt).calendar()}
    </MDTypography>
  ),
  status: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={row.active ? 'active' : 'inactive'}
        color={row.active ? 'success' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  action: (
    <MDBox ml={-1} display="flex" gap={1}>
      <MDBadge
        badgeContent="edit"
        color="info"
        variant="gradient"
        size="sm"
        onClick={() => handleEdit(row.id)}
        style={{cursor: 'pointer'}}
      />
      <MDBadge
        badgeContent="Delete"
        color="error"
        variant="gradient"
        size="sm"
        onClick={() => handleDeleteFeature(row.id)}
        style={{cursor: 'pointer'}}
      />
    </MDBox>
  ),
});

const AddUpdateFeaturesToCategory = () => {
  const [categoryDetails, setCategoryDetails] = useState({});
  const [features, setFeatures] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [active, setActive] = useState(true);

  const [openAdditionalFeatureDialog, setOpenAdditionalFeatureDialog] =
    useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [subCategories, setSubCategories] = useState([]);
  const [
    allAdditionalFeaturesWithCategoryAndSubCategory,
    setAllAdditionalFeaturesWithCategoryAndSubCategory,
  ] = useState([]);

  const {categoryId} = useParams();

  /* ----------------- Fetch Functions ----------------- */
  const fetchCategoryDetails = useCallback(async () => {
    try {
      Loading.circle('Fetching data');
      const response = await Axios.fetch(`/shop/getCategoryById/${categoryId}`);
      setCategoryDetails(response.data.data);
    } catch (err) {
      console.error(err);
      Notify.failure(err.response?.data?.message || 'Failed to fetch category');
    } finally {
      Loading.remove('Fetching data');
    }
  }, [categoryId]);

  // Fetch subcategories with their additional features.
  const fetchAllAdditionalFeaturesWithCategoryAndSubCategory =
    useCallback(async () => {
      try {
        const responseForAllAdditionalFeaturesWithCatAndSubCat =
          await Axios.fetch(`/shop/additionalFeaturesByCategory/${categoryId}`);
        const data =
          responseForAllAdditionalFeaturesWithCatAndSubCat.data?.data || [];
        setAllAdditionalFeaturesWithCategoryAndSubCategory(data);
      } catch (error) {
        console.error(error);
        Notify.failure(
          error.response?.data?.message ||
            'Failed to fetch additional features by category',
        );
      }
    }, [categoryId]);

  const fetchFeatures = useCallback(async () => {
    Loading.pulse('Fetching Features');
    try {
      const response = await Axios.fetch(
        `/shop/features/category/${categoryId}`,
      );
      setFeatures(response.data.data || []);
    } catch (err) {
      console.error(err);
      Notify.failure(err.response?.data?.message || 'Failed to fetch features');
    } finally {
      Loading.remove('Fetching Features');
    }
  }, [categoryId]);

  const fetchSubCategories = useCallback(async () => {
    try {
      const response = await Axios.fetch(`/shop/subCategories/${categoryId}`);
      setSubCategories(response.data.data || []);
    } catch (err) {
      console.error(err);
      Notify.failure(
        err.response?.data?.message || 'Failed to fetch subcategories',
      );
    }
  }, [categoryId]);

  useEffect(() => {
    fetchCategoryDetails();
    fetchFeatures();
    fetchSubCategories();
    fetchAllAdditionalFeaturesWithCategoryAndSubCategory();
  }, [
    fetchAllAdditionalFeaturesWithCategoryAndSubCategory,
    fetchCategoryDetails,
    fetchFeatures,
    fetchSubCategories,
  ]);

  /* ----------------- Main Feature Form (Create/Update) ----------------- */
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      image_url: '',
      image: null,
      active: true,
      featureDefaultPrice: 0,
    },
  });

  const onSubmitProductFeature = async data => {
    Loading.circle('Adding / Updating Feature');
    const formData = new FormData();
    formData.append('categoryId', categoryId);
    formData.append('name', data.name);
    formData.append('description', data.description);
    formData.append('active', active);
    formData.append('featureDefaultPrice', data.featureDefaultPrice);

    if (data.image_url) {
      formData.append('image_url', data.image_url);
    } else if (data.image) {
      formData.append('image', data?.image[0] || '');
    }

    try {
      if (selectedFeature?.id) {
        // Update existing feature
        const response = await Axios.post(
          `/shop/addFeatureToCategory?featureId=${selectedFeature.id}`,
          formData,
          {'Content-type': 'multipart/form-data'},
        );
        Notify.success(response.data.message || 'Feature updated successfully');
      } else {
        // Create new feature
        const response = await Axios.post(
          `/shop/addFeatureToCategory`,
          formData,
          {'Content-type': 'multipart/form-data'},
        );
        Notify.success(response.data.message || 'Feature created successfully');
      }
      setOpenDialog(false);
      fetchFeatures();
    } catch (err) {
      console.error(err);
      Notify.failure(
        err.response?.data?.message || 'Failed to add/update feature',
      );
    }
    Loading.remove('Adding / Updating Feature');
  };

  /* ----------------- Additional Features Form (Tab 0) ----------------- */
  const {
    register: registerAdditional,
    handleSubmit: handleSubmitAdditional,
    control: controlAdditional,
    reset: resetAdditional,
    formState: {errors: errorsAdditional},
  } = useForm({
    defaultValues: {
      subCategoryId: '',
      features: [
        {name: '', description: '', featureDefaultPrice: '', active: true},
      ],
    },
  });

  const {fields, append, remove} = useFieldArray({
    control: controlAdditional,
    name: 'features',
  });

  const onSubmitAddAdditionalFeatures = async data => {
    Loading.circle('Adding Additional Features');
    try {
      const subCategoryId = data.subCategoryId;
      const payload = {features: data.features};
      const response = await Axios.post(
        `/shop/addAdditionalFeaturesToSubCategory/${subCategoryId}`,
        payload,
      );
      fetchAllAdditionalFeaturesWithCategoryAndSubCategory();
      Notify.success(
        response.data.message || 'Additional features added successfully',
      );
      resetAdditional();
    } catch (err) {
      console.error(err);
      Notify.failure(
        err.response?.data?.message || 'Failed to add additional features',
      );
    }
    Loading.remove('Adding Additional Features');
  };

  /* ----------------- Create SubCategory Form (Tab 1) ----------------- */
  const {
    register: registerSubCategory,
    handleSubmit: handleSubmitSubCategory,
    reset: resetSubCategory,
    formState: {errors: errorsSubCategory},
  } = useForm({
    defaultValues: {
      subCategoryName: '',
      description: '',
      selectionType: '',
      active: true,
    },
  });

  const onSubmitCreateSubCategory = async data => {
    Loading.circle('Creating Sub Category');
    try {
      const payload = {
        categoryId,
        subCategoryName: data.subCategoryName,
        description: data.description,
        selectionType: data.selectionType,
        active: data.active,
      };

      const response = await Axios.post(
        '/shop/addUpdateSubCategoryForAdditionalFeatures',
        payload,
      );
      Notify.success(
        response.data.message || 'SubCategory created successfully',
      );
      resetSubCategory();
      fetchSubCategories();
    } catch (err) {
      console.error(err);
      Notify.failure(
        err.response?.data?.message || 'Failed to create SubCategory',
      );
    }
    Loading.remove('Creating Sub Category');
  };

  /* ----------------- Handlers for Main Features ----------------- */
  const handleUpdateFeature = id => {
    const feature = features.find(f => f.id === id);
    if (!feature) return;
    setSelectedFeature(feature);
    // Populate the form with the feature data
    setValue('name', feature.name);
    setValue('description', feature.description);
    setValue('image_url', feature.image_url || '');
    setValue('featureDefaultPrice', feature.featureDefaultPrice || 0);
    setValue('image', null); // clear file input
    setActive(feature.active);
    setOpenDialog(true);
  };

  const handleDeleteFeature = id => {
    Confirm.show(
      'Delete Feature?',
      'Do you agree to delete this feature?',
      'Yes',
      'No',
      async () => {
        try {
          const feature = features.find(f => f.id === id);
          await Axios.delete(`/shop/features/${id}?label=${encodeURIComponent(feature?.name || '')}`);
          Notify.success('Feature deleted successfully');
          setFeatures(features.filter(f => f.id !== id));
        } catch (err) {
          console.error(err);
          Notify.failure(
            err.response?.data?.message || 'Failed to delete feature',
          );
        }
      },
      () => {},
      {
        width: '320px',
        borderRadius: '8px',
        backgroundColor: '#fff',
      },
    );
  };

  const handleAddNewFeature = () => {
    setSelectedFeature(null);
    reset({
      name: '',
      description: '',
      image_url: '',
      image: null,
      active: true,
      featureDefaultPrice: 0,
    });
    setActive(true);
    setOpenDialog(true);
  };

  const handleOpenAdditionalFeatureDialog = () => {
    setTabIndex(0); // default to "Add Additional Features" tab
    setOpenAdditionalFeatureDialog(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Typography variant="p" sx={{fontSize: '16px'}}>
        <b>Category Name:</b> {categoryDetails.name} &nbsp;|&nbsp;
        <b>Description:</b> {categoryDetails.description}
      </Typography>

      {/* Main Features Section */}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <MDTypography variant="h6" color="white">
                  Add / Update Features
                </MDTypography>
                <MDBox>
                  <MDButton
                    variant="contained"
                    sx={{marginRight: '12px'}}
                    onClick={handleAddNewFeature}>
                    Add New Feature
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns,
                    rows: (features || []).map((row, idx) =>
                      getRow(
                        row,
                        idx,
                        handleUpdateFeature,
                        handleDeleteFeature,
                      ),
                    ),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  hoverEffect
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Additional Features Section */}
      <MDBox pt={5} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <MDTypography variant="h6" color="white">
                  All Additional Features
                </MDTypography>

                <MDBox>
                  <MDButton
                    variant="contained"
                    onClick={handleOpenAdditionalFeatureDialog}>
                    Manage Additional Features
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                {/* Use the AdditionalFeaturesTable component here */}
                <AdditionalFeaturesTable
                  allAdditionalFeaturesWithCategoryAndSubCategory={
                    allAdditionalFeaturesWithCategoryAndSubCategory
                  }
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* ----------------- Main Feature Dialog (Create/Update) ----------------- */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          {selectedFeature
            ? `Edit Feature: ${selectedFeature.name}`
            : 'Create Feature'}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmitProductFeature)}>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              {...register('name', {required: 'Name is required'})}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              {...register('description', {
                required: 'Description is required',
              })}
              error={!!errors.description}
              helperText={errors.description?.message}
            />
            <TextField
              label="Image URL"
              fullWidth
              margin="normal"
              {...register('image_url')}
            />
            <Input type="file" fullWidth sx={{my: 2}} {...register('image')} />
            <TextField
              label="Feature Default Price"
              fullWidth
              margin="normal"
              type="number"
              {...register('featureDefaultPrice', {
                required: 'Feature Default Price is required',
                valueAsNumber: true,
              })}
              error={!!errors.featureDefaultPrice}
              helperText={errors.featureDefaultPrice?.message}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={e => setActive(e.target.checked)}
                  name="active"
                  color="primary"
                />
              }
              label="Active"
              sx={{mt: 2}}
            />
            <DialogActions sx={{mt: 2}}>
              <MDButton
                onClick={() => setOpenDialog(false)}
                color="secondary"
                variant="outlined">
                Cancel
              </MDButton>
              <MDButton type="submit" color="info" variant="contained">
                {selectedFeature ? 'Update' : 'Add'}
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* ----------------- Additional Features Dialog (with Tabs) ----------------- */}
      <Dialog
        open={openAdditionalFeatureDialog}
        onClose={() => setOpenAdditionalFeatureDialog(false)}
        maxWidth="xl"
        fullWidth>
        <DialogTitle>Manage Additional Features</DialogTitle>
        <DialogContent>
          <Tabs
            value={tabIndex}
            onChange={(event, newValue) => setTabIndex(newValue)}
            aria-label="Additional Features Tabs">
            <Tab
              label="Manage Additional Features"
              id="additional-tab-0"
              aria-controls="additional-tabpanel-0"
            />
            <Tab
              label="Manage Sub Category"
              id="additional-tab-1"
              aria-controls="additional-tabpanel-1"
            />
          </Tabs>

          {/* Tab Panel 0: Add Additional Features */}
          <TabPanel value={tabIndex} index={0}>
            <MDTypography variant="body1" my={2}>
              Add New Additional Feature
            </MDTypography>
            <form
              onSubmit={handleSubmitAdditional(onSubmitAddAdditionalFeatures)}>
              <InputLabel
                sx={{
                  my: 1,
                  display: 'block',
                }}
                id="subCategory-select-label">
                Select SubCategory
              </InputLabel>
              <Select
                sx={{
                  height: '40px',
                  width: '100%',
                }}
                labelId="subCategory-select-label"
                id="subCategory-select"
                label="Select SubCategory"
                defaultValue={subCategories[0]?.id}
                {...registerAdditional('subCategoryId', {
                  required: 'SubCategory is required',
                })}>
                <MenuItem value="" defaultChecked>
                  Select Sub Category
                </MenuItem>
                {(subCategories || []).map(sub => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {`${sub.subCategoryName} - (${sub.selectionType})`}
                  </MenuItem>
                ))}
              </Select>
              {errorsAdditional.subCategoryId && (
                <FormHelperText>
                  {errorsAdditional.subCategoryId.message}
                </FormHelperText>
              )}

              {/* Render dynamic list of features */}
              {fields.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    border: '1px solid #ccc',
                    p: 1,
                    my: 2,
                    borderRadius: 1,
                  }}>
                  <Typography variant="subtitle1">Add Feature</Typography>
                  <TextField
                    label="Feature Name"
                    fullWidth
                    margin="normal"
                    {...registerAdditional(`features.${index}.name`, {
                      required: 'Feature name is required',
                    })}
                    error={!!errorsAdditional.features?.[index]?.name}
                    helperText={
                      errorsAdditional.features?.[index]?.name?.message
                    }
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    margin="normal"
                    {...registerAdditional(`features.${index}.description`, {
                      required: 'Description is required',
                    })}
                    error={!!errorsAdditional.features?.[index]?.description}
                    helperText={
                      errorsAdditional.features?.[index]?.description?.message
                    }
                  />
                  <TextField
                    label="Feature Default Price"
                    fullWidth
                    margin="normal"
                    type="number"
                    {...registerAdditional(
                      `features.${index}.featureDefaultPrice`,
                      {
                        required: 'Price is required',
                        valueAsNumber: true,
                      },
                    )}
                    error={
                      !!errorsAdditional.features?.[index]?.featureDefaultPrice
                    }
                    helperText={
                      errorsAdditional.features?.[index]?.featureDefaultPrice
                        ?.message
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        defaultChecked
                        {...registerAdditional(`features.${index}.active`)}
                      />
                    }
                    label="Active"
                    sx={{mt: 2}}
                  />
                  <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <IconButton
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <MDButton
                variant="outlined"
                color="info"
                onClick={() => {
                  // Check if any errors exist in the "features" fields
                  const featureErrors = errorsAdditional.features;
                  if (featureErrors && Object.keys(featureErrors).length > 0) {
                    Notify.failure(
                      'Please fix the errors in the current feature fields before adding a new one.',
                    );
                    return;
                  }
                  // If no errors, append a new feature form
                  append({
                    name: '',
                    description: '',
                    featureDefaultPrice: '',
                    active: true,
                  });
                }}>
                Add Another Feature
              </MDButton>

              <DialogActions sx={{mt: 2}}>
                <MDButton
                  onClick={() => setOpenAdditionalFeatureDialog(false)}
                  color="secondary"
                  variant="outlined">
                  Cancel
                </MDButton>
                <MDButton type="submit" color="info" variant="contained">
                  Add Features
                </MDButton>
              </DialogActions>
            </form>

            {/* Data table for all additional features within the dialog */}
            <AdditionalFeaturesTable
              allAdditionalFeaturesWithCategoryAndSubCategory={
                allAdditionalFeaturesWithCategoryAndSubCategory
              }
            />
          </TabPanel>

          {/* Tab Panel 1: Create SubCategory */}
          <TabPanel value={tabIndex} index={1}>
            <form onSubmit={handleSubmitSubCategory(onSubmitCreateSubCategory)}>
              <TextField
                label="SubCategory Name"
                fullWidth
                margin="normal"
                {...registerSubCategory('subCategoryName', {
                  required: 'SubCategory Name is required',
                })}
                error={!!errorsSubCategory.subCategoryName}
                helperText={errorsSubCategory.subCategoryName?.message}
              />
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                {...registerSubCategory('description', {
                  required: 'Description is required',
                })}
                error={!!errorsSubCategory.description}
                helperText={errorsSubCategory.description?.message}
              />
              <TextField
                label="Selection Type"
                fullWidth
                margin="normal"
                sx={{height: 42}}
                select
                {...registerSubCategory('selectionType', {
                  required: 'Selection Type is required',
                })}
                error={!!errorsSubCategory.selectionType}
                helperText={errorsSubCategory.selectionType?.message}>
                <MenuItem value="">Select selection type</MenuItem>
                <MenuItem value="radio">Radio</MenuItem>
                <MenuItem value="checkbox">Checkbox</MenuItem>
                <MenuItem value="range">Range</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Switch defaultChecked {...registerSubCategory('active')} />
                }
                label="Active"
                sx={{mt: 2}}
              />
              <DialogActions sx={{mt: 2}}>
                <MDButton
                  onClick={() => setOpenAdditionalFeatureDialog(false)}
                  color="secondary"
                  variant="outlined">
                  Cancel
                </MDButton>
                <MDButton type="submit" color="info" variant="contained">
                  Create SubCategory
                </MDButton>
              </DialogActions>
            </form>

            {/* Data table for subcategories */}
            <AdditionalFeatureCategoriesTable
              subCategories={subCategories}
              setSubCategories={setSubCategories}
              categoryId={categoryId}
            />
          </TabPanel>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AddUpdateFeaturesToCategory;
